import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import './App.css';

const NUM_ROWS = 80;
const NUM_COLS = 160;
const TILE_SIZE = 20; // Canvas tile size (unscaled)
const COLORS = ['black', 'white','red', 'green', 'blue', 'yellow', 'orange', 'purple'];

const backend_url='https://kristiansen.wtf/api';

const socket = io('https://kristiansen.wtf');

export default function App() {
  const [selectedColor, setSelectedColor] = useState('red');
  const tilesRef = useRef([]);          // store tile colors
  const canvasRef = useRef(null);
const dragThreshold = 10;         // pixels
const wasDraggingRef = useRef(false);
  const zoomRef = useRef(0.8);          // start zoomed out
  const offsetRef = useRef({ x: 0, y: 0 });
  const draggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const lastOffsetRef = useRef({ x: 0, y: 0 });
  const pinchStartRef = useRef(null);
const VISIBLE_TILE_THRESHOLD = 0.5; // do not draw tiles that are smaller than 0.5px on screen
function clampOffset(offset, zoom, canvas) {
  const gridWidth = NUM_COLS * TILE_SIZE * zoom;
  const gridHeight = NUM_ROWS * TILE_SIZE * zoom;

  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;

  // If the grid is smaller than the canvas, center it
  const minX = Math.min(0, canvasWidth - gridWidth);
  const maxX = 0;

  const minY = Math.min(0, canvasHeight - gridHeight);
  const maxY = 0;

  return {
    x: Math.min(Math.max(offset.x, minX), maxX),
    y: Math.min(Math.max(offset.y, minY), maxY)
  };
}
const handleTouchStart = (e) => {
  if (e.touches.length === 1) {
    // Single finger -> pan or tile placement
    const touch = e.touches[0];
    draggingRef.current = true;
    dragStartRef.current = { x: touch.clientX, y: touch.clientY };
    lastOffsetRef.current = { ...offsetRef.current };
  } else if (e.touches.length === 2) {
    // Pinch to zoom
    const [touch1, touch2] = e.touches;
    pinchStartRef.current = {
      distance: Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      ),
      zoom: zoomRef.current,
      center: {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2,
      },
    };
  }
};

const handleTouchMove = (e) => {
  e.preventDefault(); // prevent scrolling

  if (e.touches.length === 1 && draggingRef.current) {
    const touch = e.touches[0];
    const dx = touch.clientX - dragStartRef.current.x;
    const dy = touch.clientY - dragStartRef.current.y;

    if (Math.hypot(dx, dy) > dragThreshold) {
      wasDraggingRef.current = true;
    }

    offsetRef.current = clampOffset(
      {
        x: lastOffsetRef.current.x + dx,
        y: lastOffsetRef.current.y + dy,
      },
      zoomRef.current,
      canvasRef.current
    );

    draw();
  } else if (e.touches.length === 2) {
    // Pinch to zoom
    const [touch1, touch2] = e.touches;
    const dist = Math.hypot(
      touch2.clientX - touch1.clientX,
      touch2.clientY - touch1.clientY
    );
    const factor = dist / pinchStartRef.current.distance;
    let newZoom = pinchStartRef.current.zoom * factor;
    newZoom = Math.min(Math.max(newZoom, 0.8), 5);

    const worldX = (pinchStartRef.current.center.x - offsetRef.current.x) / zoomRef.current;
    const worldY = (pinchStartRef.current.center.y - offsetRef.current.y) / zoomRef.current;

    zoomRef.current = newZoom;
    offsetRef.current.x = pinchStartRef.current.center.x - worldX * newZoom;
    offsetRef.current.y = pinchStartRef.current.center.y - worldY * newZoom;

    offsetRef.current = clampOffset(offsetRef.current, newZoom, canvasRef.current);

    draw();
  }
};

const handleTouchEnd = (e) => {
  draggingRef.current = false;
  pinchStartRef.current = null;

  if (!wasDraggingRef.current && e.changedTouches.length === 1) {
    const touch = e.changedTouches[0];
    handleCanvasClick({
      clientX: touch.clientX,
      clientY: touch.clientY,
    });
  }

  wasDraggingRef.current = false;
};
const draw = () => {
  const canvas = canvasRef.current;
  const ctx = canvas.getContext("2d");
  const zoom = zoomRef.current;
  const offset = offsetRef.current;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  ctx.save();
  ctx.translate(offset.x, offset.y);
  ctx.scale(zoom, zoom);

  // Compute visible tile range (culling)
  const startCol = Math.max(0, Math.floor(-offset.x / zoom / TILE_SIZE));
  const startRow = Math.max(0, Math.floor(-offset.y / zoom / TILE_SIZE));

  const endCol = Math.min(
    NUM_COLS,
    Math.ceil((canvas.width - offset.x) / zoom / TILE_SIZE)
  );
  const endRow = Math.min(
    NUM_ROWS,
    Math.ceil((canvas.height - offset.y) / zoom / TILE_SIZE)
  );

  for (let row = startRow; row < endRow; row++) {
    for (let col = startCol; col < endCol; col++) {
      const tileIndex = row * NUM_COLS + col;
      const color = tilesRef.current[tileIndex] || "white";

      // Skip super tiny tiles when zoomed out
      if (zoom * TILE_SIZE < VISIBLE_TILE_THRESHOLD) continue;

      ctx.fillStyle = color;
      ctx.fillRect(col * TILE_SIZE, row * TILE_SIZE, TILE_SIZE, TILE_SIZE);
    }
  }

  // Draw grid only if zoomed in enough
  if (zoom > 0.2) {
    ctx.strokeStyle = "#222";
    ctx.lineWidth = 1 / zoom;
    ctx.beginPath();

    for (let col = startCol; col <= endCol; col++) {
      ctx.moveTo(col * TILE_SIZE, startRow * TILE_SIZE);
      ctx.lineTo(col * TILE_SIZE, endRow * TILE_SIZE);
    }

    for (let row = startRow; row <= endRow; row++) {
      ctx.moveTo(startCol * TILE_SIZE, row * TILE_SIZE);
      ctx.lineTo(endCol * TILE_SIZE, row * TILE_SIZE);
    }

    ctx.stroke();
  }

  ctx.restore();
};
  // Load initial tiles
  useEffect(() => {
    axios.get(backend_url + "/tiles").then(res => {
      const arr = new Array(NUM_ROWS * NUM_COLS).fill("white");

      res.data.forEach(tile => {
        arr[tile.id] = tile.color;
      });

      tilesRef.current = arr;
      draw();
    });
  }, []);

  // Listen to socket updates
  useEffect(() => {
    const handler = ({ id, color }) => {
      tilesRef.current[id] = color;
      draw();
    };

    socket.on("tileUpdated", handler);
    return () => socket.off("tileUpdated", handler);
  }, []);

  // Get tile index from mouse
  const getTileIndexFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - offsetRef.current.x) / zoomRef.current;
    const y = (e.clientY - rect.top - offsetRef.current.y) / zoomRef.current;

    const col = Math.floor(x / TILE_SIZE);
    const row = Math.floor(y / TILE_SIZE);

    if (col < 0 || row < 0 || col >= NUM_COLS || row >= NUM_ROWS) return null;
    return row * NUM_COLS + col;
  };

  // Paint tile
  const handleCanvasClick = async (e) => {
    const idx = getTileIndexFromEvent(e);
    if (idx === null) return;
    if (wasDraggingRef.current) {
        wasDraggingRef.current = false;

    return; // Your existing tile-placement function
    }
      wasDraggingRef.current = false;


    tilesRef.current[idx] = selectedColor;
    draw();

    await axios.post(`${backend_url}/tiles/${idx}`, {
      color: selectedColor
    });
  };

const handleWheel = (e) => {
  e.preventDefault();

  const canvas = canvasRef.current;
  const rect = canvas.getBoundingClientRect();

  const mouseX = e.clientX - rect.left;
  const mouseY = e.clientY - rect.top;

  const zoomFactor = 1.1;
  const oldZoom = zoomRef.current;
  let newZoom = oldZoom * (e.deltaY < 0 ? zoomFactor : 1/zoomFactor);
  newZoom = Math.min(Math.max(newZoom, 0.8), 5);

  // Convert mouse position to world (grid) coordinates
  const worldX = (mouseX - offsetRef.current.x) / oldZoom;
  const worldY = (mouseY - offsetRef.current.y) / oldZoom;

  // Update zoom
  zoomRef.current = newZoom;

  // Offset so the point under the cursor stays fixed
  offsetRef.current.x = mouseX - worldX * newZoom;
  offsetRef.current.y = mouseY - worldY * newZoom;

  // Clamp pan if you use clamping
  offsetRef.current = clampOffset(offsetRef.current, newZoom, canvas);

  draw();
};
  // Pan controls
  const handleMouseDown = (e) => {
    draggingRef.current = true;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    lastOffsetRef.current = { ...offsetRef.current };
  };

  const handleMouseMove = (e) => {
    if (!draggingRef.current) return;

    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
  // Detect drag (avoid false clicks)
  if (Math.hypot(dx, dy) > dragThreshold) {
    wasDraggingRef.current = true;
  }
const canvas = canvasRef.current;

offsetRef.current = clampOffset(
  {
    x: lastOffsetRef.current.x + dx,
    y: lastOffsetRef.current.y + dy
  },
  zoomRef.current,
  canvas
);

draw();
  };
useEffect(() => {
  draw();
}, [selectedColor]);
  const stopDragging = () => (draggingRef.current = false);
  return (
    <div className="App">
      <div className="header">
      <h1>What the fudge</h1>

      <div className="palette">
        {COLORS.map((c) => (
          <button
            key={c}
            style={{ backgroundColor: c }}
            className={selectedColor === c ? "selected" : ""}
            onClick={() => {setSelectedColor(c); draw()}}
          />
        ))}
      </div>
      </div>


      <canvas
        ref={canvasRef}
        width={window.innerWidth}
        height={window.innerHeight-200}
        onClick={handleCanvasClick}

        onWheel={handleWheel}
  onTouchStart={handleTouchStart}
  onTouchMove={handleTouchMove}
  onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={stopDragging}
        onMouseLeave={stopDragging}

        style={{
          border: "1px solid black",
          cursor: draggingRef.current ? "grabbing" : "grab",
          background: "#eee"
        }}
      />
    </div>
  );
}
